<template>
  <BDLayout>
    <div class="transaction">
      <section id="bid-checklists-overview">
        <div class="container-fluid px-4">

          <!-- Header -->
          <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <div>
              <h6 class="fw-bold mb-0">Bid Checklists Overview</h6>
            </div>
            <div class="d-flex flex-row align-items-center gap-2">
              <input
                v-model="search"
                class="form-control form-control-sm"
                style="min-width:220px"
                placeholder="Search opportunity or assignee…"
              />
              <select v-model="filterStatus" class="form-select form-select-sm" style="min-width:140px">
                <option value="">All Statuses</option>
                <option value="NOT_STARTED">Not Started</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="text-center py-5 text-muted">
            <span class="spinner-border spinner-border-sm me-2"></span> Loading checklists…
          </div>

          <!-- Error state -->
          <div v-else-if="loadError" class="alert alert-danger d-flex align-items-center gap-3">
            <i class="fas fa-exclamation-circle fa-lg"></i>
            <div class="flex-1">
              <strong>Failed to load checklists</strong><br>
              <small class="font-monospace">{{ loadError }}</small>
            </div>
            <button class="btn btn-sm btn-outline-danger ms-auto" @click="load">Retry</button>
          </div>

          <!-- Empty state -->
          <div v-else-if="filtered.length === 0" class="text-center py-5 text-muted">
            <i class="fas fa-clipboard-list fa-2x mb-3 d-block opacity-50"></i>
            No bid preparations found.
          </div>

          <!-- Cards grid -->
          <div v-else class="checklist-overview-grid">
            <div
              v-for="prep in filtered"
              :key="prep.id"
              class="checklist-card"
            >
              <!-- Card header -->
              <div class="checklist-card-header">
                <div class="flex-1 min-w-0">
                  <RouterLink
                    :to="`/admin/bid/opportunities/${prep.opportunity.id}`"
                    class="opp-title"
                  >{{ prep.opportunity.tenderTitle }}</RouterLink>
                  <div class="opp-meta">
                    <span class="opp-ref">{{ prep.opportunity.tenderReference }}</span>
                    <span class="opp-client" v-if="prep.opportunity.client">
                      &nbsp;· {{ prep.opportunity.client.name }}
                    </span>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2 flex-shrink-0">
                  <span :class="['prep-status-badge', statusClass(prep.status)]">{{ formatStatus(prep.status) }}</span>
                  <button
                    class="btn btn-sm btn-link p-0 toggle-btn"
                    @click="toggleExpand(prep.id)"
                    :title="expanded[prep.id] ? 'Collapse' : 'Expand'"
                  >
                    <i :class="expanded[prep.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                  </button>
                </div>
              </div>

              <!-- Summary row -->
              <div class="checklist-card-summary">
                <!-- Assignee -->
                <div class="summary-item">
                  <i :class="['fas', 'fa-user-tie', 'me-1', prep.owner ? 'text-success' : 'text-danger']"></i>
                  <span v-if="prep.owner" class="assignee-name text-success">{{ fullName(prep.owner) }}</span>
                  <span v-else class="text-danger fw-semibold fst-italic">Unassigned</span>
                </div>
                <!-- Deadline -->
                <div class="summary-item" v-if="prep.opportunity.submissionDeadline">
                  <i class="fas fa-calendar-alt me-1 text-muted"></i>
                  <span :class="{ 'text-danger fw-semibold': isOverdue(prep.opportunity.submissionDeadline) }">
                    {{ formatDate(prep.opportunity.submissionDeadline) }}
                  </span>
                </div>
                <!-- Progress bar -->
                <div class="summary-item flex-grow-1">
                  <div class="progress-wrapper">
                    <div class="progress checklist-progress">
                      <div
                        class="progress-bar"
                        :class="progressBarClass(prep.checklists)"
                        :style="{ width: progressPct(prep.checklists) + '%' }"
                      ></div>
                    </div>
                    <span class="progress-label">
                      {{ doneCount(prep.checklists) }}/{{ prep.checklists.length }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Expandable Kanban board -->
              <transition name="slide-down">
                <div v-if="expanded[prep.id]" class="kanban-body">

                  <!-- Empty state -->
                  <div v-if="prep.checklists.length === 0" class="kanban-empty-prep">
                    No checklist items.
                    <RouterLink :to="`/admin/bid/opportunities/${prep.opportunity.id}?tab=preparation`">Add items</RouterLink>
                  </div>

                  <!-- Kanban columns -->
                  <div v-else class="kanban-board-mini">

                    <!-- To Do column -->
                    <div
                      class="kanban-col-mini"
                      :class="{ 'kanban-col-mini--over': dragOverColKey === prep.id + '-todo' }"
                      @dragover.prevent="dragOverColKey = prep.id + '-todo'"
                      @dragleave="onDragLeave(prep.id, 'todo')"
                      @drop.prevent="onDrop(prep, 'todo')"
                    >
                      <div class="kanban-col-mini-header kanban-col-mini-header--todo">
                        <span class="kanban-col-mini-dot kanban-col-mini-dot--todo"></span>
                        <span>To Do</span>
                        <span class="kanban-col-mini-count">{{ todoItemsFor(prep).length }}</span>
                      </div>

                      <div
                        v-for="item in todoItemsFor(prep)"
                        :key="item.id"
                        class="kanban-card-mini kanban-card-mini--todo"
                        draggable="true"
                        @dragstart="onDragStart(item)"
                        @dragend="dragItemId = null"
                      >
                        <div class="kanban-card-mini-body">
                          <i class="fas fa-grip-vertical kanban-mini-grip"></i>
                          <span class="kanban-card-mini-text">{{ item.itemName }}</span>
                        </div>
                        <div class="kanban-card-mini-footer">
                          <span v-if="item.dueDate" :class="['mini-due', { 'mini-due--overdue': isOverdue(item.dueDate) }]">
                            <i class="fas fa-clock me-1"></i>{{ formatDate(item.dueDate) }}
                          </span>
                          <span v-else class="mini-due text-muted">No due date</span>
                          <button
                            class="kanban-mini-action-btn kanban-mini-action-btn--done"
                            :disabled="toggling[item.id]"
                            @click="toggleItem(item)"
                            title="Mark as Done"
                          >
                            <span v-if="toggling[item.id]" class="spinner-border spinner-border-sm"></span>
                            <span v-else>Done <i class="fas fa-check"></i></span>
                          </button>
                        </div>
                      </div>

                      <div v-if="todoItemsFor(prep).length === 0" class="kanban-mini-empty">
                        <i class="fas fa-inbox"></i> All done!
                      </div>
                    </div>

                    <!-- Done column -->
                    <div
                      class="kanban-col-mini"
                      :class="{ 'kanban-col-mini--over': dragOverColKey === prep.id + '-done' }"
                      @dragover.prevent="dragOverColKey = prep.id + '-done'"
                      @dragleave="onDragLeave(prep.id, 'done')"
                      @drop.prevent="onDrop(prep, 'done')"
                    >
                      <div class="kanban-col-mini-header kanban-col-mini-header--done">
                        <span class="kanban-col-mini-dot kanban-col-mini-dot--done"></span>
                        <span>Done</span>
                        <span class="kanban-col-mini-count">{{ doneItemsFor(prep).length }}</span>
                      </div>

                      <div
                        v-for="item in doneItemsFor(prep)"
                        :key="item.id"
                        class="kanban-card-mini kanban-card-mini--done"
                        draggable="true"
                        @dragstart="onDragStart(item)"
                        @dragend="dragItemId = null"
                      >
                        <div class="kanban-card-mini-body">
                          <i class="fas fa-grip-vertical kanban-mini-grip"></i>
                          <span class="kanban-card-mini-text kanban-card-mini-text--done">{{ item.itemName }}</span>
                        </div>
                        <div class="kanban-card-mini-footer">
                          <span v-if="item.dueDate" class="mini-due text-muted">
                            <i class="fas fa-clock me-1"></i>{{ formatDate(item.dueDate) }}
                          </span>
                          <span v-else class="mini-due text-muted">No due date</span>
                          <button
                            class="kanban-mini-action-btn kanban-mini-action-btn--undo"
                            :disabled="toggling[item.id]"
                            @click="toggleItem(item)"
                            title="Move back to To Do"
                          >
                            <span v-if="toggling[item.id]" class="spinner-border spinner-border-sm"></span>
                            <span v-else><i class="fas fa-undo"></i> Undo</span>
                          </button>
                        </div>
                      </div>

                      <div v-if="doneItemsFor(prep).length === 0" class="kanban-mini-empty">
                        <i class="fas fa-check-circle"></i> Nothing yet
                      </div>
                    </div>

                  </div>
                </div>
              </transition>
            </div>
          </div>

        </div>
      </section>
    </div>
  </BDLayout>
</template>

<script>
import BDLayout from '@/components/BDLayout.vue'
import bidApi from '@/api/bid'

export default {
  name: 'BidChecklistsOverview',
  components: { BDLayout },

  data() {
    return {
      preparations: [],
      loading: true,
      loadError: null,
      search: '',
      filterStatus: '',
      expanded: {},
      toggling: {},
      dragItemId: null,
      dragOverColKey: null,
    }
  },

  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase()
      return this.preparations.filter((p) => {
        if (this.filterStatus && p.status !== this.filterStatus) return false
        if (!q) return true
        const inTitle = p.opportunity?.tenderTitle?.toLowerCase().includes(q)
        const inRef = p.opportunity?.tenderReference?.toLowerCase().includes(q)
        const inClient = p.opportunity?.client?.name?.toLowerCase().includes(q)
        const inOwner = p.owner ? this.fullName(p.owner).toLowerCase().includes(q) : false
        const inItems = p.checklists?.some(
          (c) =>
            c.itemName?.toLowerCase().includes(q) ||
            (c.owner ? this.fullName(c.owner).toLowerCase().includes(q) : false)
        )
        return inTitle || inRef || inClient || inOwner || inItems
      })
    },
    todoItemsFor() {
      return (prep) => (prep.checklists || []).filter(i => !i.isCompleted)
    },
    doneItemsFor() {
      return (prep) => (prep.checklists || []).filter(i => i.isCompleted)
    },
  },

  async mounted() {
    await this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.loadError = null
      try {
        const data = await bidApi.getAllPreparations()
        this.preparations = data
        // Auto-expand if few records
        if (data.length <= 5) {
          data.forEach((p) => { this.expanded[p.id] = true })
        }
      } catch (e) {
        console.error('Failed to load preparations', e)
        this.loadError = e?.message || (typeof e === 'string' ? e : JSON.stringify(e))
      } finally {
        this.loading = false
      }
    },

    toggleExpand(id) {
      this.expanded = { ...this.expanded, [id]: !this.expanded[id] }
    },

    async toggleItem(item) {
      this.toggling = { ...this.toggling, [item.id]: true }
      try {
        await bidApi.updateChecklist(item.id, { isCompleted: !item.isCompleted })
        item.isCompleted = !item.isCompleted
      } catch (e) {
        console.error('Failed to update checklist item', e)
      } finally {
        this.toggling = { ...this.toggling, [item.id]: false }
      }
    },

    fullName(user) {
      if (!user) return ''
      return user.name || user.email || ''
    },

    onDragStart(item) {
      this.dragItemId = item.id
    },

    onDragLeave(prepId, col) {
      if (this.dragOverColKey === prepId + '-' + col) {
        this.dragOverColKey = null
      }
    },

    async onDrop(prep, targetCol) {
      this.dragOverColKey = null
      if (!this.dragItemId) return
      const item = prep.checklists.find(i => i.id === this.dragItemId)
      if (!item) return
      const shouldBeDone = targetCol === 'done'
      if (item.isCompleted === shouldBeDone) return
      await this.toggleItem(item)
    },

    doneCount(checklists) {
      return (checklists || []).filter((c) => c.isCompleted).length
    },

    progressPct(checklists) {
      if (!checklists || checklists.length === 0) return 0
      return Math.round((this.doneCount(checklists) / checklists.length) * 100)
    },

    progressBarClass(checklists) {
      const pct = this.progressPct(checklists)
      if (pct === 100) return 'bg-success'
      if (pct >= 50) return 'bg-warning'
      return 'bg-danger'
    },

    formatStatus(status) {
      const map = { NOT_STARTED: 'Not Started', IN_PROGRESS: 'In Progress', COMPLETED: 'Completed' }
      return map[status] || status
    },

    statusClass(status) {
      const map = { NOT_STARTED: 'status-notstarted', IN_PROGRESS: 'status-inprogress', COMPLETED: 'status-done' }
      return map[status] || ''
    },

    formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
    },

    isOverdue(d) {
      return d && new Date(d) < new Date()
    },
  },
}
</script>

<style scoped>
#bid-checklists-overview {
  padding: 24px 0;
}

.checklist-overview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
  align-items: start;
}
@media (max-width: 768px) {
  .checklist-overview-grid {
    grid-template-columns: 1fr;
  }
}

/* Card */
.checklist-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,.05);
  transition: box-shadow .2s;
}
.checklist-card:hover {
  box-shadow: 0 3px 11px rgba(0,0,0,.09);
}

/* Card header */
.checklist-card-header {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 13px 16px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #eef2f7;
}
.opp-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #1a202c;
  text-decoration: none;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 480px;
}
.opp-title:hover { color: #2563eb; text-decoration: underline; }
.opp-meta {
  font-size: 0.78rem;
  color: #64748b;
  margin-top: 2px;
}
.opp-ref { font-weight: 600; color: #475569; }
.flex-1 { flex: 1; min-width: 0; }

/* Status badge */
.prep-status-badge {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: .04em;
}
.status-notstarted { background: #f1f5f9; color: #64748b; }
.status-inprogress { background: #fef3c7; color: #92400e; }
.status-done       { background: #dcfce7; color: #166534; }

.toggle-btn { color: #94a3b8; font-size: 0.85rem; }
.toggle-btn:hover { color: #475569; }

/* Summary row */
.checklist-card-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 10px 16px;
  background: #fff;
}
.summary-item {
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  color: #374151;
}
.assignee-name { font-weight: 600; }

/* Progress */
.progress-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.checklist-progress {
  flex: 1;
  height: 7px;
  border-radius: 99px;
  background: #e2e8f0;
}
.progress-label { font-size: 0.78rem; font-weight: 700; color: #475569; white-space: nowrap; }

/* ── Kanban body (expanded) ────────────────────────── */
.kanban-body {
  border-top: 1px solid #f1f5f9;
  padding: 11px 13px;
  background: #f4f5f7;
}

.kanban-empty-prep {
  padding: 11px 4px;
  font-size: 0.85rem;
  color: #94a3b8;
}

.kanban-board-mini {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

/* Column */
.kanban-col-mini {
  background: #ebecf0;
  border-radius: 7px;
  padding: 8px;
  min-height: 80px;
  border: 2px solid transparent;
  transition: border-color .15s, background .15s;
}
.kanban-col-mini--over {
  border-color: #4c9aff;
  background: #e8f0fe;
}

.kanban-col-mini-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 2px 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
}
.kanban-col-mini-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.kanban-col-mini-dot--todo { background: #ff991f; }
.kanban-col-mini-dot--done { background: #36b37e; }
.kanban-col-mini-header--todo { color: #974f0c; }
.kanban-col-mini-header--done { color: #006644; }
.kanban-col-mini-count {
  margin-left: auto;
  background: rgba(0,0,0,.1);
  border-radius: 99px;
  padding: 0 6px;
  font-size: 10px;
  color: inherit;
}

/* Cards */
.kanban-card-mini {
  background: #fff;
  border-radius: 5px;
  padding: 8px 10px;
  margin-bottom: 6px;
  box-shadow: 0 1px 2px rgba(0,0,0,.1);
  border-left: 3px solid transparent;
  cursor: grab;
  transition: box-shadow .15s, transform .1s;
}
.kanban-card-mini:last-child { margin-bottom: 0; }
.kanban-card-mini:hover { box-shadow: 0 2px 6px rgba(0,0,0,.15); transform: translateY(-1px); }
.kanban-card-mini:active { cursor: grabbing; }
.kanban-card-mini--todo { border-left-color: #ff991f; }
.kanban-card-mini--done { border-left-color: #36b37e; opacity: 0.85; }

.kanban-card-mini-body {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 6px;
}
.kanban-mini-grip {
  color: #c1c7d0;
  font-size: 10px;
  margin-top: 2px;
  flex-shrink: 0;
}
.kanban-card-mini-text {
  font-size: 11px;
  color: #172b4d;
  line-height: 1.4;
  word-break: break-word;
}
.kanban-card-mini-text--done {
  text-decoration: line-through;
  color: #7a869a;
}

.kanban-card-mini-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.mini-due {
  font-size: 10px;
  color: #6b778c;
}
.mini-due--overdue {
  color: #de350b;
  font-weight: 700;
}

.kanban-mini-action-btn {
  border: none;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: background .15s;
}
.kanban-mini-action-btn:disabled { opacity: 0.5; cursor: default; }
.kanban-mini-action-btn--done { background: #e3fcef; color: #006644; }
.kanban-mini-action-btn--done:hover:not(:disabled) { background: #abf5d1; }
.kanban-mini-action-btn--undo { background: #fff4e5; color: #974f0c; }
.kanban-mini-action-btn--undo:hover:not(:disabled) { background: #ffe2bd; }

.kanban-mini-empty {
  text-align: center;
  font-size: 11px;
  color: #97a0af;
  padding: 11px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

/* Transition */
.slide-down-enter-active,
.slide-down-leave-active { transition: all .2s ease; overflow: hidden; }
.slide-down-enter-from,
.slide-down-leave-to { max-height: 0; opacity: 0; }
.slide-down-enter-to,
.slide-down-leave-from { max-height: 1200px; opacity: 1; }
</style>
